import type {BlockedContributorsBanner} from '@github-ui/code-view-types'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ChevronDownIcon, ChevronUpIcon} from '@primer/octicons-react'
import {Banner, Button, Link} from '@primer/react'
import {useId, useState} from 'react'

import styles from './BlockedContributors.module.css'

export interface BlockedContributorsProps {
  banner: BlockedContributorsBanner
}

export function BlockedContributors({banner}: BlockedContributorsProps) {
  const [expanded, setExpanded] = useState(false)
  const detailsId = useId()
  const {totalCount, contributors, additionalLogins, manageBlockedUsersPath, disableWarningPath} = banner

  if (totalCount === 0) return null

  const title =
    totalCount === 1
      ? `A user you’ve blocked has previously contributed to this repository.`
      : `${totalCount} users you’ve blocked have previously contributed to this repository.`

  return (
    <Banner
      variant="warning"
      className={styles.banner}
      primaryAction={
        <Button
          variant="invisible"
          aria-expanded={expanded}
          aria-controls={detailsId}
          onClick={() => setExpanded(prev => !prev)}
          trailingVisual={expanded ? ChevronUpIcon : ChevronDownIcon}
          className={styles.actionButton}
        >
          {expanded ? 'Collapse' : 'Expand'}
        </Button>
      }
    >
      <Banner.Title className={styles.title}>{title}</Banner.Title>
      <Banner.Description>
        {expanded && (
          <div id={detailsId} className={styles.contributors}>
            <div className={styles.contributorsList}>
              {contributors.map(contributor => (
                <Link
                  key={contributor.login}
                  href={contributor.commitsPath}
                  className={`Link--primary ${styles.contributorLink}`}
                >
                  <GitHubAvatar src={contributor.avatarUrl} size={20} alt="" className={styles.avatar} />
                  <strong>{contributor.name}</strong>
                </Link>
              ))}
              {additionalLogins.length > 0 && (
                <details className="text-bold">
                  <summary>+ {additionalLogins.length} more</summary>
                  <ul className={styles.additionalLoginsList}>
                    {additionalLogins.map(login => (
                      <li key={login}>{login}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
            <div>
              <Link href={manageBlockedUsersPath} className={styles.actionLink}>
                Manage blocked users
              </Link>
              {' or '}
              <Link href={disableWarningPath} className={styles.actionLink}>
                disable this warning
              </Link>
            </div>
          </div>
        )}
      </Banner.Description>
    </Banner>
  )
}
